import { parseAsJSON } from "../../lib/parse";
import { rejectBad } from "../error";
import { withRetry } from "../retry";
import { exhaust } from "./pagination";
import { APIContext } from "./types";

const RETRY_BACKOFF = [250, 250, 500, 1000];

type Fetch = typeof fetch;

export type APIContextOptions = {
  buildURL: (path: string) => URL;
  getToken: () => Promise<string>;
  onError: (error: unknown) => void;
  fetch?: Fetch;
};

export function createContext({
  buildURL,
  getToken,
  onError,
  fetch = global.fetch,
}: APIContextOptions): APIContext {
  async function addAuth(headers: Headers) {
    const token = await getToken();
    headers.set("authorization", `Bearer ${token}`);
  }

  async function execute(request: Request, context: { signal: AbortSignal }) {
    const { signal } = context;
    const fetchWithRetry = withRetry(fetch, RETRY_BACKOFF);

    await addAuth(request.headers);

    const flight = fetchWithRetry(request, { signal }).then((response) => {
      rejectBad(request, response);
      return response;
    });

    flight.catch(onError);

    return flight;
  }

  function fetchRaw(request: Request) {
    const abort = new AbortController();
    return {
      result: execute(request, { signal: abort.signal }),
      abandon() {
        abort.abort();
      },
    };
  }

  function fetchAs<T>(
    request: Request,
    middleware: (response: Response) => Promise<T>
  ) {
    const abort = new AbortController();
    return {
      result: execute(request, { signal: abort.signal }).then(middleware),
      abandon() {
        abort.abort();
      },
    };
  }

  function fetchJSON<T>(request: Request) {
    return fetchAs(request, (response) => {
      return parseAsJSON<T>(response);
    });
  }

  function fetchPagedJSON<T>(request: Request) {
    const abort = new AbortController();
    return {
      result: exhaust<T>((request) => {
        return execute(request, { signal: abort.signal });
      }, request),
      abandon() {
        abort.abort();
      },
    };
  }

  return {
    buildURL,
    fetchRaw,
    fetchAs,
    fetchJSON,
    fetchPagedJSON,
  };
}
